import { SVG_ENUM } from "enums";
import { EditPersonalDataModal } from "features/users";
import { useDisconnectSocial, useGetSocialAccounts } from "hooks/auth";
import React, { useState } from "react";
import { Button, Modal, SVG } from "shared";
import { UserType } from "types/UsersTypes";
import { getPrefixFromUuid } from "utils";
import { useGetPhonePrefixes } from "hooks/phone";

interface LoginOptionsProps {
  user: UserType | undefined;
  handleGoBack: () => void;
}

const LoginOptions: React.FC<LoginOptionsProps> = ({ user, handleGoBack }) => {
  const { data: prefixNumbers } = useGetPhonePrefixes();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [disconnectProvider, setDisconnectProvider] = useState<
    "google" | "apple" | null
  >(null);
  const { mutate: disconnectSocial } = useDisconnectSocial();
  const { data: socialAccounts } = useGetSocialAccounts();

  const hasEmail = !!user?.email;
  const googleAccount = socialAccounts?.find((a) => a.provider === "google");
  const appleAccount = socialAccounts?.find((a) => a.provider === "apple");

  const renderSocialSection = (
    provider: "google" | "apple",
    account: { name: string; email: string } | undefined,
    svgIcon: SVG_ENUM,
    connectLabel: string,
  ) => {
    if (account) {
      return (
        <div className="profile__social-connected">
          <p className="profile__social-connected-name">{account.name}</p>
          <div className="profile__social-connected-row">
            <span className="profile__social-connected-email">
              {account.email}
            </span>
            <span
              className="profile__social-connected-remove"
              onClick={() => setDisconnectProvider(provider)}
            >
              Usuń metodę logowania
            </span>
          </div>
        </div>
      );
    }

    return (
      <button
        type="button"
        className="login-wrapper__social-btn"
        onClick={() => {
          window.location.href = `${process.env.REACT_APP_API_URL}/auth/${provider}/connect`;
        }}
      >
        <SVG type={svgIcon} />
        {connectLabel}
      </button>
    );
  };

  return (
    <div className="profile__right-wrapper">
      <EditPersonalDataModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        user={user || null}
      />
      <Modal
        isOpened={!!disconnectProvider}
        onClose={() => setDisconnectProvider(null)}
      >
        <>
          <div className="modal-heading">Usuń metodę logowania</div>
          <div className="modal-description">
            Czy jesteś pewien, że chcesz usunąć metodę logowania przez{" "}
            {disconnectProvider === "google" ? "Google" : "Apple"}?
          </div>
          <div className="modal-buttons">
            <Button
              className="button--stroke button--rounded"
              onClick={() => setDisconnectProvider(null)}
              label="Anuluj"
            />
            <Button
              className="button--black button--rounded"
              onClick={() => {
                if (disconnectProvider) {
                  disconnectSocial({ provider: disconnectProvider });
                  setDisconnectProvider(null);
                }
              }}
              label="Potwierdź"
            />
          </div>
        </>
      </Modal>
      <div className="profile__right-content">
        <div className="profile__right-back" onClick={() => handleGoBack()}>
          <SVG type={SVG_ENUM.ARROW_LEFT} /> Wróć
        </div>
        <h2 className="profile__menu-header">
          <span className="profile__menu-icon profile__menu-icon--right">
            <SVG type={SVG_ENUM.LOGIN_OPTIONS} />
          </span>
          Opcje logowania
        </h2>

        <p className="profile__section-title">
          Logowanie za pomocą kodu dostępu na numer telefonu
        </p>
        <table className="profile__table">
          <tr>
            <td className="profile__table-label">Numer telefonu:</td>
            <td className="profile__table-cell">
              {user?.phonePrefixUuid
                ? getPrefixFromUuid(user.phonePrefixUuid, prefixNumbers)
                : ""}{" "}
              {user?.phoneNumber || "-"}
            </td>
          </tr>
          <tr>
            <td className="profile__table-label">Email:</td>
            <td className="profile__table-cell">{user?.email || "-"}</td>
          </tr>
        </table>

        {!hasEmail && !googleAccount && !appleAccount ? (
          <p className="profile__social-no-email profile__social-no-email--top">
            Aby połączyć konto z Apple lub Google, potrzebny jest adres email.{" "}
            <span
              className="profile__social-no-email-link"
              onClick={() => setIsEditModalOpen(true)}
            >
              Kliknij tutaj, aby go dodać.
            </span>
          </p>
        ) : (
          <>
            <p className="profile__section-title profile__section-title--alternative">
              Logowanie za pomocą konta Google
            </p>
            {renderSocialSection(
              "google",
              googleAccount,
              SVG_ENUM.GOOGLE_ICON,
              "Połącz konto z kontem Google",
            )}

            <p className="profile__section-title profile__section-title--alternative">
              Logowanie za pomocą konta Apple
            </p>
            {renderSocialSection(
              "apple",
              appleAccount,
              SVG_ENUM.APPLE_ICON,
              "Połącz konto z kontem Apple",
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LoginOptions;
